import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from "react";
import { renderTermsMarkdown } from "./markdown";
export const TermsCheckbox = ({ audience, baseURL, checked, onChange })=>{
    const [terms, setTerms] = useState(null);
    const [showModal, setShowModal] = useState(false);
    useEffect(()=>{
        let cancelled = false;
        (async ()=>{
            try {
                const res = await fetch(`${baseURL}/auth/terms?audience=${encodeURIComponent(audience)}`);
                if (!cancelled && res.ok) setTerms(await res.json());
            } catch  {
            /* link will still open the modal but with empty body */ }
        })();
        return ()=>{
            cancelled = true;
        };
    }, [
        audience,
        baseURL
    ]);
    const html = useMemo(()=>terms ? renderTermsMarkdown(terms.content_markdown) : '', [
        terms
    ]);
    const modal = showModal && terms ? /*#__PURE__*/ _jsx("div", {
        className: "fixed inset-0 z-[9999] flex items-end sm:items-center sm:justify-center bg-black/55 backdrop-blur-sm p-0 sm:p-4",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "w-full sm:max-w-lg max-h-[88vh] sm:max-h-[80vh] overflow-hidden bg-white dark:bg-[#111] rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col border border-gray-200 dark:border-gray-800",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "px-5 pt-5 pb-3 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-[#111] flex items-start justify-between gap-3",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-start gap-3 min-w-0",
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: "flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-50 dark:bg-blue-950 text-primary",
                                    children: /*#__PURE__*/ _jsxs("svg", {
                                        viewBox: "0 0 24 24",
                                        fill: "none",
                                        className: "h-5 w-5",
                                        stroke: "currentColor",
                                        strokeWidth: 2,
                                        strokeLinecap: "round",
                                        strokeLinejoin: "round",
                                        "aria-hidden": "true",
                                        children: [
                                            /*#__PURE__*/ _jsx("path", {
                                                d: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                                            }),
                                            /*#__PURE__*/ _jsx("polyline", {
                                                points: "14 2 14 8 20 8"
                                            }),
                                            /*#__PURE__*/ _jsx("line", {
                                                x1: "8",
                                                y1: "13",
                                                x2: "16",
                                                y2: "13"
                                            }),
                                            /*#__PURE__*/ _jsx("line", {
                                                x1: "8",
                                                y1: "17",
                                                x2: "13",
                                                y2: "17"
                                            })
                                        ]
                                    })
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "min-w-0",
                                    children: [
                                        /*#__PURE__*/ _jsx("h2", {
                                            className: "font-semibold text-base sm:text-lg text-gray-900 dark:text-white truncate",
                                            children: terms.title
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            className: "mt-0.5 text-xs text-gray-500 dark:text-gray-400",
                                            children: [
                                                "Version ",
                                                terms.version
                                            ]
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            onClick: ()=>setShowModal(false),
                            className: "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white text-2xl leading-none p-1 -m-1",
                            "aria-label": "Close",
                            children: "×"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "px-5 py-4 overflow-y-auto flex-1 text-sm bg-white dark:bg-[#111]",
                    dangerouslySetInnerHTML: {
                        __html: html
                    }
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "px-5 py-3 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-[#111] flex flex-col-reverse sm:flex-row gap-2 sm:justify-end",
                    children: [
                        /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            onClick: ()=>setShowModal(false),
                            className: "px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800",
                            children: "Close"
                        }),
                        /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            onClick: ()=>{
                                onChange(true);
                                setShowModal(false);
                            },
                            className: "px-4 py-2 text-sm font-semibold rounded-lg bg-primary text-white hover:opacity-90",
                            children: "I agree"
                        })
                    ]
                })
            ]
        })
    }) : null;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                role: "checkbox",
                "aria-checked": checked,
                tabIndex: 0,
                onClick: (e)=>{
                    // Don't toggle when the inner "Terms & Conditions" button is the
                    // click target — that opens the modal instead.
                    if (e.target.closest('button[data-terms-link]')) return;
                    onChange(!checked);
                },
                onKeyDown: (e)=>{
                    if (e.key === ' ' || e.key === 'Enter') {
                        e.preventDefault();
                        onChange(!checked);
                    }
                },
                className: 'flex items-start gap-2.5 rounded-lg border px-3 py-2.5 cursor-pointer select-none transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary ' + (checked ? 'border-primary bg-blue-50/60 dark:bg-blue-950/40' : 'border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'),
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: 'mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ' + (checked ? 'bg-primary border-primary text-white' : 'border-gray-400 dark:border-gray-500 bg-white dark:bg-gray-900'),
                        "aria-hidden": "true",
                        children: checked && /*#__PURE__*/ _jsx("svg", {
                            viewBox: "0 0 20 20",
                            fill: "none",
                            className: "h-3 w-3",
                            stroke: "currentColor",
                            strokeWidth: 3,
                            strokeLinecap: "round",
                            strokeLinejoin: "round",
                            children: /*#__PURE__*/ _jsx("polyline", {
                                points: "4 11 8 15 16 6"
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        className: "text-xs sm:text-sm text-gray-700 dark:text-gray-300 leading-snug",
                        children: [
                            "I have read and agree to the Swiftmedix",
                            ' ',
                            /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                "data-terms-link": "1",
                                onClick: (e)=>{
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setShowModal(true);
                                },
                                className: "text-primary font-medium underline-offset-2 hover:underline",
                                children: "Terms & Conditions"
                            }),
                            "."
                        ]
                    })
                ]
            }),
            modal
        ]
    });
};
export default TermsCheckbox;

//# sourceMappingURL=TermsCheckbox.js.map